import { Link, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  Bell,
  Briefcase,
  Bug,
  CreditCard,
  ExternalLink,
  RefreshCw,
  Shield,
  Smartphone,
  User,
  Users,
} from "lucide-react";
import AdminSidebar from "@/components/AdminSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  appLabel,
  appOf,
  buildUserSignals,
  deriveAppInstallState,
  describeSubscriptionPlan,
  summariseCrewSwitches,
  summariseProducerSwitches,
} from "@produce/shared";
import { useAdminUserDetails } from "@/hooks/useAdminUserDetails";
import { sentryUserIssuesUrl } from "@/lib/sentryLinks";
import CrewMembershipList from "@/components/admin/userDetail/CrewMembershipList";
import DeviceList from "@/components/admin/userDetail/DeviceList";
import FactGrid from "@/components/admin/userDetail/FactGrid";
import ProductionCard from "@/components/admin/userDetail/ProductionCard";
import SignalList from "@/components/admin/userDetail/SignalList";
import SwitchGrid from "@/components/admin/userDetail/SwitchGrid";
import {
  formatAgo,
  formatDay,
  formatFlag,
  formatStamp,
} from "@/components/admin/userDetail/format";

/**
 * Everything the platform knows about one user, on one page.
 *
 * Exists to answer support questions without a database session: where a
 * producer got stuck in the wizard, whether a crew member's invitation
 * actually arrived, whether they have the app, and which switches they have
 * turned off. Read-only by design — the actions (roles, deletion) stay on the
 * user list, so this page cannot change anything it is describing.
 */

function Section({
  title,
  icon: Icon,
  children,
  action,
}: {
  title: string;
  icon: typeof User;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <Card className="p-6">
      <div className="mb-4 flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <Icon className="h-4 w-4" />
          {title}
        </h2>
        {action}
      </div>
      {children}
    </Card>
  );
}

/** Sidebar, width and back-link — the frame all three states render inside. */
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <AdminSidebar />
        <div className="flex-1 p-8">
          <div className="mx-auto max-w-6xl space-y-6">
            <Button variant="ghost" size="sm" asChild className="px-0">
              <Link to="/admin/users">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to users
              </Link>
            </Button>
            {children}
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
}

/** A timestamp with "(3 days ago)" beside it — the format both dates use. */
function Stamp({ value }: { value: string | null }) {
  const ago = formatAgo(value);
  return (
    <span>
      {formatStamp(value)}
      {ago && <span className="ml-1 text-xs text-muted-foreground">({ago})</span>}
    </span>
  );
}

export default function UserDetail() {
  const { userId } = useParams<{ userId: string }>();
  const { data, isLoading, error, refetch, isFetching } = useAdminUserDetails(userId);

  if (isLoading) {
    return (
      <Shell>
        <div className="space-y-4">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </Shell>
    );
  }

  if (error || !data) {
    return (
      <Shell>
        <Card className="p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 text-destructive" />
            <div>
              <p className="font-semibold">Could not load this user</p>
              <p className="text-sm text-muted-foreground">
                {error instanceof Error ? error.message : "Unknown error"}
              </p>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
                Try again
              </Button>
            </div>
          </div>
        </Card>
      </Shell>
    );
  }

  const { profile, roles, subscription, devices, producer, crew } = data;
  const signals = buildUserSignals(data);
  const install = deriveAppInstallState(crew?.settings ?? null, devices);
  const planLabel = describeSubscriptionPlan(subscription);
  const sentryUrl = sentryUserIssuesUrl(profile.email);
  const productions = producer?.productions ?? [];
  const productionTotal = producer?.production_count ?? productions.length;
  const memberships = crew?.memberships ?? [];
  const activeCoProducers = (producer?.co_producers ?? []).filter((c) => !c.revoked_at);

  return (
    <Shell>
      {/* Identity */}
      <Card className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">{profile.full_name || "No name"}</h1>
            <p className="text-muted-foreground">{profile.email}</p>
            {profile.company_name && (
              <p className="text-sm text-muted-foreground">{profile.company_name}</p>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {roles.length === 0 && <Badge variant="outline">No roles</Badge>}
              {roles.map((role) => (
                <Badge key={role} variant={role === "admin" ? "destructive" : "secondary"}>
                  {role}
                </Badge>
              ))}
              {profile.manual_producer_access && <Badge variant="outline">Manual access</Badge>}
              {planLabel && <Badge variant="outline">{planLabel}</Badge>}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
              <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            {sentryUrl && (
              <Button variant="outline" size="sm" asChild>
                <a href={sentryUrl} target="_blank" rel="noreferrer">
                  <Bug className="mr-2 h-4 w-4" />
                  Errors in Sentry
                  <ExternalLink className="ml-2 h-3 w-3" />
                </a>
              </Button>
            )}
          </div>
        </div>
      </Card>

      <Section title="Signals" icon={AlertTriangle}>
        <SignalList signals={signals} />
      </Section>

      <Section title="Account" icon={User}>
        <FactGrid
          facts={[
            { label: "User id", value: <span className="font-mono text-xs">{profile.id}</span> },
            { label: "Phone", value: profile.phone || "—" },
            { label: "Signed up", value: formatDay(profile.created_at) },
            { label: "Last sign-in", value: <Stamp value={data.last_sign_in_at} /> },
            {
              label: "Email verified",
              value: profile.email_verified_at ? formatDay(profile.email_verified_at) : "Never",
            },
            { label: "Onboarding guide", value: formatFlag(profile.guide_completed) },
            { label: "Briefing guide", value: formatFlag(profile.briefing_guide_completed) },
            { label: "Manual producer access", value: formatFlag(profile.manual_producer_access) },
          ]}
        />
      </Section>

      <Section title="Subscription" icon={CreditCard}>
        {subscription ? (
          <div className="space-y-4">
            <FactGrid
              facts={[
                { label: "Plan", value: planLabel ?? "Free" },
                {
                  label: "Tier",
                  value: subscription.plan_tier ? subscription.plan_tier.toUpperCase() : "—",
                },
                { label: "Status", value: <Badge>{subscription.plan_status}</Badge> },
                { label: "Started", value: formatDay(subscription.subscription_start) },
                {
                  label: subscription.plan_status === "trial" ? "Trial ends" : "Renews",
                  value: formatDay(
                    subscription.plan_status === "trial"
                      ? subscription.trial_ends_at
                      : subscription.subscription_end,
                  ),
                },
                { label: "Discount code", value: subscription.discount_code_used || "—" },
                { label: "Discount ends", value: formatDay(subscription.discount_end_date) },
              ]}
            />
            {subscription.stripe_customer_id && (
              <Button variant="outline" size="sm" asChild>
                <a
                  href={`https://dashboard.stripe.com/customers/${subscription.stripe_customer_id}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  View in Stripe
                  <ExternalLink className="ml-2 h-3 w-3" />
                </a>
              </Button>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No subscription record.</p>
        )}
      </Section>

      <Section title="App & devices" icon={Smartphone}>
        <div className="space-y-4">
          <FactGrid
            facts={[
              { label: "Crew app", value: install.hasApp ? "Installed" : "Never opened" },
              {
                label: "Type",
                value: install.isNative ? "Native app" : install.hasApp ? "Web" : "—",
              },
              { label: "App version", value: install.appVersion || "—" },
              { label: "Platform", value: install.platform || "—" },
              { label: "Last app sync", value: <Stamp value={install.lastSyncAt} /> },
              { label: "Crew app push devices", value: `${install.deviceCount}` },
            ]}
          />
          <DeviceList devices={devices} />
          {data.update_events.length > 0 && (
            <div>
              <h3 className="mb-2 text-sm font-semibold">Recent app updates</h3>
              <ul className="space-y-1 text-sm">
                {data.update_events.slice(0, 5).map((event, index) => (
                  <li key={`${event.created_at}-${index}`} className="flex flex-wrap gap-2">
                    <span className="text-muted-foreground">{formatStamp(event.created_at)}</span>
                    <span>{event.event_type}</span>
                    <span className="text-muted-foreground">{appLabel(appOf(event))}</span>
                    {event.app_version && (
                      <span className="text-muted-foreground">v{event.app_version}</span>
                    )}
                    {event.error_message && (
                      <span className="text-destructive">{event.error_message}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Section>

      <Section title="Notification & tracking settings" icon={Bell}>
        <div className="space-y-6">
          <div>
            {/* "Producer web portal", not "Producer app": a native producer
                app is planned, and these settings belong to the web one. */}
            <h3 className="mb-2 text-sm font-semibold">Producer web portal</h3>
            <SwitchGrid toggles={summariseProducerSwitches(producer?.settings ?? null)} />
            {producer?.settings && (
              <p className="mt-2 text-sm text-muted-foreground">
                {producer.settings.quiet_hours_enabled &&
                  `Quiet hours ${producer.settings.quiet_hours_start ?? "?"} – ${producer.settings.quiet_hours_end ?? "?"} · `}
                Tracking accuracy: {producer.settings.tracking_accuracy ?? "default"} · Frequency:{" "}
                {producer.settings.tracking_frequency ?? "default"}
              </p>
            )}
          </div>
          <div>
            <h3 className="mb-2 text-sm font-semibold">Crew app</h3>
            <SwitchGrid toggles={summariseCrewSwitches(crew?.settings ?? null)} />
            {crew && (
              <p className="mt-2 text-sm text-muted-foreground">
                Location sharing consent: {formatFlag(crew.location_sharing_consent)} · Reminder
                lead time: {crew.settings?.reminder_minutes ?? crew.notification_lead_time ?? "—"}{" "}
                min
              </p>
            )}
          </div>
        </div>
      </Section>

      <Section title={`Productions (${productionTotal})`} icon={Briefcase}>
        {productions.length === 0 ? (
          <p className="text-sm text-muted-foreground">This user owns no productions.</p>
        ) : (
          <div className="space-y-4">
            {productionTotal > productions.length && (
              <p className="text-sm text-muted-foreground">
                Showing the {productions.length} most recent of {productionTotal} productions.
              </p>
            )}
            {productions.map((production) => (
              <ProductionCard key={production.id} production={production} />
            ))}
          </div>
        )}
      </Section>

      {activeCoProducers.length > 0 && (
        <Section title={`Co-producers (${activeCoProducers.length})`} icon={Shield}>
          <ul className="space-y-2 text-sm">
            {activeCoProducers.map((coProducer) => (
              <li
                key={`${coProducer.briefing_id}-${coProducer.label}`}
                className="flex flex-wrap items-center gap-2 rounded-md border p-3"
              >
                <div className="min-w-0">
                  <div className="font-medium">{coProducer.label}</div>
                  <div className="text-muted-foreground">
                    {coProducer.festival_name || "Untitled production"}
                  </div>
                </div>
                <span className="ml-auto text-muted-foreground">
                  last login {formatStamp(coProducer.last_login_at)}
                </span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      <Section title={`Invited as crew (${memberships.length})`} icon={Users}>
        <CrewMembershipList memberships={memberships} />
      </Section>
    </Shell>
  );
}
